import React from 'react';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import {
  ButtonPriority as ButtonPRIORITY,
  ButtonSize as ButtonSIZE,
} from 'wix-ui-tpa/cssVars';
import { ParticipantStep } from '@wix/ambassador-challenges-v1-participant/types';
import { Challenges } from '../../../../../editor/types/Experiments';
import utils, { getFlatStepsList } from '../../views/utils';

import { IControl } from './utils/types';
import { isSectionLocked } from '../../../../../selectors/sections';
import { SidebarLayoutButton } from '../Buttons/SidebarLayoutButton/SidebarLayoutButton';
import { SidebarControlsContainer } from '../SidebarControlsContainer/SidebarControlsContainer';
import { FCWithChildren } from '@wix/challenges-web-library';
import { useChallengeData } from '../../../../../contexts/storage-contexts/Challenge';
import { StepsNavigation } from './components/StepsNavigation';
import {
  FocusPoint,
  useFocusManager,
} from '../../../../../contexts/FocusManager/useFocusManager';

export interface ISectionControls extends IControl {
  currentSectionId: string;
  onStepChosen(firstSectionStep: ParticipantStep): void;
}

export const SectionControls: FCWithChildren<ISectionControls> = (props) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const { isMobile } = useEnvironment();
  const { setNextFocusPoint } = useFocusManager();
  const {
    challengeData: { challenge },
  } = useChallengeData();
  const chosenSection = props.sections.find(
    (s) => s.id === props.currentSectionId,
  );

  if (
    experiments.enabled(Challenges.enableDripContent) &&
    isSectionLocked(chosenSection)
  ) {
    return null;
  }

  const flatSteps = getFlatStepsList({
    listParticipantSections: props.sections,
    participantSteps: props.steps,
  });

  const firstSectionStep = chosenSection?.steps?.[0];

  const isFirstSectionStepHidden =
    firstSectionStep &&
    utils.isStepHidden(challenge, flatSteps, firstSectionStep);

  const nextEntity = utils.getFollowingEntity(
    firstSectionStep?.id,
    chosenSection?.id,
    {
      listParticipantSections: props.sections,
      participantSteps: props.steps,
    },
    false,
  );

  if (isFirstSectionStepHidden) {
    return null;
  }

  return (
    <SidebarControlsContainer>
      <SidebarLayoutButton
        fullWidth={isMobile}
        key={chosenSection?.id}
        priority={ButtonPRIORITY.primary}
        size={isMobile ? ButtonSIZE.small : ButtonSIZE.medium}
        onClick={async () => {
          setNextFocusPoint(FocusPoint.Top);
          if (firstSectionStep) {
            props.onStepChosen(firstSectionStep);
          } else {
            await props.onNextEntity(nextEntity);
          }
        }}
      >
        {t('challenge.page.steps.continue')}
      </SidebarLayoutButton>

      <StepsNavigation
        stepId={null}
        sectionId={chosenSection?.id}
        onChangeEntity={props.onNextEntity}
      />
    </SidebarControlsContainer>
  );
};
